import { Field, Select } from '@noorden-insurtech/alba-design-system';
import { X } from 'lucide-react';
import { memo, useCallback } from 'react';
import type { CapitalGlobalSelectOption } from '@/features/quotes/schemas/capital-global/coverages-catalog';

/* eslint-disable no-unused-vars -- callback parameter names document arity for callers */
type Props = {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  items?: CapitalGlobalSelectOption[];
};
/* eslint-enable no-unused-vars */

/**
 * Select opcional com limpeza explícita (X) e deselect no dropdown.
 * Usado em Assistência Funeral e Capitalização — campos não obrigatórios no step 3.
 */
export const CapitalGlobalClearableSelect = memo(function CapitalGlobalClearableSelect({
  label,
  value,
  onValueChange,
  placeholder,
  disabled,
  required = false,
  error,
  items = [],
}: Props) {
  const hasValue = Boolean(value);

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      onValueChange(details.value[0] ?? '');
    },
    [onValueChange]
  );

  return (
    <Field.Root invalid={Boolean(error)}>
      <Field.Label className='text-sm font-bold text-alba-black-500'>
        {label}
        {required && <span className='text-alba-error-red-500'>*</span>}
      </Field.Label>
      <Select.Root
        value={value ? [value] : []}
        onValueChange={handleValueChange}
        positioning={{ sameWidth: true }}
        items={items}
        disabled={disabled}
        deselectable
      >
        <Select.Control
          className='relative flex w-full group'
          data-has-current-value={hasValue}
        >
          <Select.Trigger className='group-data-[has-current-value=true]:pr-9'>
            <Select.ValueText placeholder={placeholder} />
            <Select.Indicator />
          </Select.Trigger>
          <Select.ClearTrigger
            type='button'
            className='absolute right-2 top-1/2 z-10 -translate-y-1/2 items-center justify-center rounded p-1 text-alba-gray-600 hover:bg-alba-gray-200 hover:text-alba-black-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-alba-blue-500 group-data-[has-current-value=true]:visible flex invisible'
            aria-label='Limpar seleção'
          >
            <X className='h-4 w-4' strokeWidth={1.6} />
          </Select.ClearTrigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {items.map(option => (
              <Select.Item key={option.value} item={option}>
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
      {error ? <Field.ErrorText>{error}</Field.ErrorText> : null}
    </Field.Root>
  );
});
